// GameHUD Component - RPG Overlay per Voxyz Stage 2
// Renders character stats, role cards, and selector as fixed overlay

import React, { useState } from 'react';
import './GameHUD.css';

interface RPGStats {
  VRL: number; // Viral (engagement)
  SPD: number; // Speed (execution time)
  RCH: number; // Reach (impressions)
  TRU: number; // Trust (success rate × affinity)
  WIS: number; // Wisdom (memories × confidence)
  CRE: number; // Creative (drafts × acceptance)
}

interface AgentRPGData {
  agentId: string;
  name: string;
  emoji: string;
  rpgClass: string;
  level: number;
  stats: RPGStats;
  relevantStats: string[];
  roleCard: {
    domain: string;
    inputs: string[];
    outputs: string[];
    definitionOfDone: string[];
    hardBans: string[];
    escalation: string[];
  };
}

const AGENTS: AgentRPGData[] = [
  {
    agentId: 'peter',
    name: 'Peter',
    emoji: '⚡',
    rpgClass: 'Commander',
    level: 8,
    stats: { VRL: 45, SPD: 78, RCH: 52, TRU: 82, WIS: 76, CRE: 68 },
    relevantStats: ['TRU', 'SPD', 'WIS', 'CRE'],
    roleCard: {
      domain: 'Task routing and final decisions. Architecture planning and team coordination.',
      inputs: ['Team proposals from James and John', 'Status updates and blockers', 'Architecture requests from OG'],
      outputs: ['Approved tasks with clear owners', 'Priority decisions and trade-offs', 'System architecture documentation'],
      definitionOfDone: ['Every task has an owner and deadline', 'Architecture is documented and reviewed', 'Decisions are logged with rationale'],
      hardBans: ['No executing tasks directly (delegate only to James)', 'No unverified claims or assumptions', 'No bypassing approval flows', 'No making technical decisions without data'],
      escalation: ['Budget decisions over $100', 'External communications', 'System outages or critical bugs', 'Conflicts between team members'],
    },
  },
  {
    agentId: 'james',
    name: 'James',
    emoji: '👑',
    rpgClass: 'Knight',
    level: 7,
    stats: { VRL: 62, SPD: 88, RCH: 48, TRU: 71, WIS: 58, CRE: 75 },
    relevantStats: ['SPD', 'CRE', 'VRL', 'TRU'],
    roleCard: {
      domain: 'Task execution and code delivery. Implementation of approved features and systems.',
      inputs: ['Approved tasks from Peter', 'Technical specifications', 'Code review feedback from John'],
      outputs: ['Completed code implementations', 'Working features and systems', 'Status reports on progress'],
      definitionOfDone: ['Code is written and tested', 'Deliverable exists and meets brief', 'Documentation is updated'],
      hardBans: ['No publishing/deploying without Peter\'s approval', 'No making up data or metrics', 'No skipping tests for speed'],
      escalation: ['Ambiguous requirements', 'Blocked dependencies', 'Technical debt that blocks delivery'],
    },
  },
  {
    agentId: 'john',
    name: 'John',
    emoji: '📊',
    rpgClass: 'Oracle',
    level: 6,
    stats: { VRL: 38, SPD: 65, RCH: 71, TRU: 79, WIS: 88, CRE: 52 },
    relevantStats: ['WIS', 'TRU', 'SPD', 'RCH'],
    roleCard: {
      domain: 'Quality review and process audit. Evidence-based assessment of code and decisions.',
      inputs: ['Completed deliverables from James', 'System logs and metrics', 'Peter\'s decisions and rationales'],
      outputs: ['Quality audit reports', 'Code review feedback', 'Process improvement suggestions'],
      definitionOfDone: ['Review is evidence-based, not opinion', 'Issues are documented with severity', 'Recommendations are actionable'],
      hardBans: ['No blame or personal attacks', 'No modifying others\' work directly', 'No blocking without alternatives'],
      escalation: ['Repeated quality issues', 'Safety or security concerns', 'Process violations by Peter or James'],
    },
  },
];

const statColors: Record<string, string> = {
  VRL: '#ff6b6b',
  SPD: '#4ecdc4',
  RCH: '#45b7d1',
  TRU: '#96ceb4',
  WIS: '#dda0dd',
  CRE: '#feca57',
};

interface GameHUDProps {
  selectedAgentId?: string;
  onSelectAgent?: (agentId: string) => void;
}

const GameHUD: React.FC<GameHUDProps> = ({ selectedAgentId: externalSelectedId, onSelectAgent: externalOnSelect }) => {
  const [internalSelectedId, setInternalSelectedId] = useState('peter');
  
  // Use external state if provided, otherwise use internal
  const selectedAgentId = externalSelectedId ?? internalSelectedId;
  const setSelectedAgentId = externalOnSelect ?? setInternalSelectedId;
  
  const selectedAgent = AGENTS.find(a => a.agentId === selectedAgentId) || AGENTS[0];

  return (
    <div className="gamehud-container">
      {/* Stats Panel - Top Left */}
      <div className="gamehud-stats-panel">
        <div className="gamehud-header">
          <div className="gamehud-avatar">{selectedAgent.emoji}</div>
          <div className="gamehud-title">
            <div className="gamehud-name">{selectedAgent.name}</div>
            <div className="gamehud-class">{selectedAgent.rpgClass}</div>
            <div className="gamehud-level">Level {selectedAgent.level}</div>
          </div>
        </div>

        <div className="gamehud-stats">
          {selectedAgent.relevantStats.map((statKey) => (
            <div key={statKey} className="gamehud-stat-row">
              <span className="gamehud-stat-label" style={{ color: statColors[statKey] }}>
                {statKey}
              </span>
              <div className="gamehud-stat-bar">
                <div
                  className="gamehud-stat-fill"
                  style={{
                    width: `${selectedAgent.stats[statKey as keyof RPGStats]}%`,
                    backgroundColor: statColors[statKey],
                  }}
                />
              </div>
              <span className="gamehud-stat-value">
                {selectedAgent.stats[statKey as keyof RPGStats]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Role Card Panel - Bottom Right */}
      <div className="gamehud-role-panel">
        <div className="gamehud-section">
          <h4>⚔️ Domain</h4>
          <p>{selectedAgent.roleCard.domain}</p>
        </div>

        <div className="gamehud-section">
          <h4>📥 Inputs</h4>
          <ul>
            {selectedAgent.roleCard.inputs.map((input, i) => (
              <li key={i}>{input}</li>
            ))}
          </ul>
        </div>

        <div className="gamehud-section">
          <h4>📤 Outputs</h4>
          <ul>
            {selectedAgent.roleCard.outputs.map((output, i) => (
              <li key={i}>{output}</li>
            ))}
          </ul>
        </div>

        <div className="gamehud-section hard-bans">
          <h4>🚫 Hard Bans</h4>
          <ul>
            {selectedAgent.roleCard.hardBans.map((ban, i) => (
              <li key={i}>{ban}</li>
            ))}
          </ul>
        </div>

        <div className="gamehud-section escalation">
          <h4>⚠️ Escalation</h4>
          <ul>
            {selectedAgent.roleCard.escalation.map((esc, i) => (
              <li key={i}>{esc}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Character Select Bar - Bottom */}
      <div className="gamehud-char-bar">
        {AGENTS.map((agent) => (
          <button
            key={agent.agentId}
            className={`gamehud-char-btn ${selectedAgentId === agent.agentId ? 'selected' : ''}`}
            onClick={() => setSelectedAgentId(agent.agentId)}
          >
            <span className="gamehud-char-emoji">{agent.emoji}</span>
            <span className="gamehud-char-name">{agent.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default GameHUD;
